import type {
  RegistryAllocation,
  RegistryEntityDetail,
  RegistryFundOpportunityAllocations,
} from '../api/registryEntityTypes';
import { RegistryApiError } from '../api/registryApi';
import { sumAmounts, summariseEntity, type EntitySource } from './entitySource';

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);

function summariseFunds(allocations: RegistryAllocation[]): RegistryFundOpportunityAllocations[] {
  const byFund = new Map<string, RegistryAllocation[]>();
  for (const allocation of allocations) {
    const key = allocation.fundOpportunity.externalId;
    byFund.set(key, [...(byFund.get(key) ?? []), allocation]);
  }
  return Array.from(byFund.values(), (group) => ({
    externalId: group[0].fundOpportunity.externalId,
    name: group[0].fundOpportunity.name,
    status: group[0].fundOpportunity.status,
    closeDate: null,
    allocationCount: group.length,
    entityCount: new Set(group.map((allocation) => allocation.investmentEntityExternalId)).size,
    totalAllocated: sumAmounts(group.map((allocation) => allocation.allocatedAmount)),
  })).sort(byName);
}

/** Serves the prototype's mock platform entities through the same interface as the admin API. */
export function createMockEntitySource(entities: RegistryEntityDetail[]): EntitySource {
  const allocations = entities.flatMap((entity) => entity.allocations);
  return {
    sample: true,
    listEntities: async () => entities.map(summariseEntity).sort(byName),
    getEntity: async (entityId) => {
      const entity = entities.find((candidate) => candidate.externalId === entityId);
      if (!entity) throw new RegistryApiError('Investment entity not found', 404, 'NOT_FOUND');
      return entity;
    },
    listFundOpportunities: async () => summariseFunds(allocations),
    listFundOpportunityAllocations: async (fundOpportunityId) =>
      allocations
        .filter((allocation) => allocation.fundOpportunity.externalId === fundOpportunityId)
        .sort((a, b) => a.investmentEntityName.localeCompare(b.investmentEntityName)),
  };
}
